import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { IDestroyInventoryDialogSubmittedEvent } from '../../interfaces/destroy-inventory-dialog-submitted-event.interface';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { Select, Store } from '@ngxs/store';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';
import { Observable } from 'rxjs';
import { IProvider } from 'src/app/shared/interfaces/provider.interface';
import { CreatePlaceOfDestruction, GetPlacesOfDestructionList } from 'src/app/shared/states/orders/orders.actions';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { IDestroyIngredient } from '../../interfaces/destroy-ingredient.interface';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { IReceiveCompoundFormRow } from '../../interfaces/receive-compound-form-row';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { IDestroyIngredientSubmittedEvent } from '../../interfaces/destroy-ingredient-submitted-event.interface';
import { PinService } from 'src/app/shared/services/pin.service';
import { ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { IWasteIngredientInput } from '../../interfaces/waste-ingredient-input.interface';
import Decimal from 'decimal.js';

type IDestroyIngredientControl = FormControls<IDestroyIngredient>;

@UntilDestroy()
@Component({
  selector: 'app-destroy-ingredient-dialog',
  templateUrl: './destroy-ingredient-dialog.component.html',
  styleUrls: ['./destroy-ingredient-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InventoryLocationsService]
})
export class DestroyIngredientDialogComponent implements OnInit {
  @Input() ndcToWaste: IWasteIngredientInput;
  @Output() submit: EventEmitter<IDestroyIngredientSubmittedEvent> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();
  
  form: FormGroup;

  @Select(OrdersState.placesOfDestruction) placesOfDestruction$: Observable<IProvider[]>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  
  @ViewChild('dialog') dialog;

  private signerPinChannel = 'signerPin';
  private witnessPinChannel = 'witnessPin';

  onHand = undefined;

  private pin: number;
  /**
   *
   */
  constructor(
    private store: Store, 
    private formattedDatePipe: FormattedDatePipe, 
    private transactionsService: TransactionsService, 
    private cdr: ChangeDetectorRef,
    private inventoryLocationsService: InventoryLocationsService,
    private pinService: PinService
  ) {
  }
  
  ngOnInit(): void {
    if (!this.store.selectSnapshot(OrdersState.placesOfDestruction)?.length) {
      this.store.dispatch(new GetPlacesOfDestructionList());
    }

    this.form = new FormGroup({
      placeOfDestructionId: new FormControl('', [autocompleteValidator]),
      nameOfDestruction: new FormControl(this.ndcToWaste.nameOfDestruction),
      methodOfDestruction: new FormControl('', [Validators.required]),
      transactionDate: new FormControl(new Date(), [Validators.required]),
      transactionDateDisplayValue: new FormControl(this.formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy')),
      addedQuantity: new FormControl('', [Validators.required, Validators.max(this.ndcToWaste.onHand)]),
      note: new FormControl(null),
    });

    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.dialogId === this.signerPinChannel && res?.success) {
        this.pinService.openWitnessDialog(this.witnessPinChannel);
        this.pin = res.pin;
      } else if (res?.dialogId === this.witnessPinChannel && res?.success) {
        const row = this.form.getRawValue();
        this.submit.next({
          ...row,
          locationID: this.ndcToWaste.locationID,
          drugID: this.ndcToWaste.drugID,
          placeOfDestructionId: row.placeOfDestructionId.id,
          pin: this.pin,
          witnessPin: res.pin
         });
         this.pinService.closeDialog();
      }
    })

    this.form.get('addedQuantity').valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(val => {
      if (val) {
        this.onHand = new Decimal(this.ndcToWaste.onHand).sub(new Decimal(val)).toNumber();
      }
    })
  }

  submitForm(): void {
   if (this.form.invalid) {
    highlightRequiredControlsWithErrors(this.form);
    return;
   }

   const row = this.form.getRawValue();
   const payload = {
    transactionDate: row.transactionDate,
    inventoryActions: [{
        drugId: this.ndcToWaste.drugID,
        addedQuantity: row.addedQuantity,
        activityType: ActivityTypesEnum.WastedDestroyed,
        locationId : this.ndcToWaste.locationID
      }]
    }

   this.store.dispatch(new ValidateTransactionDateForInventories(payload))
    .pipe(untilDestroyed(this))
    .subscribe(resp => {
      const validateTrDateResponse = resp.inventory?.response;
      if (
        validateTrDateResponse?.success &&
        validateTrDateResponse?.value?.length
      ) {
        this.form.get('transactionDateDisplayValue')?.setErrors({causedNegativeBalance: true});
        this.transactionsService.handleTransactionDateErrors(validateTrDateResponse.value);
        this.cdr.markForCheck();
      } else {
        this.pinService.openPinDialog(this.signerPinChannel);
      }
    });
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }

  addPlaceOfDestruction(name: string): void {
    this.store.dispatch(new CreatePlaceOfDestruction(name));
  }

  saveAddedNote(note: string): void {
    this.form.get('note')?.setValue(note);
  }

  removeNote(): void {
    this.form.get('note')?.setValue(null);
  }

  addedQtyTabbedEntered(ev): void {
    ev.preventDefault()
    this.form.valid ? this.dialog?.focusSubmitButton() : this.dialog?.focusCancelButton()
  }

  now(): Date {
    return new Date();
  }

}
